import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { io } from "socket.io-client";
import { toast } from "react-toastify";

// Initial state
const initialState = {
  isConnected: false,
  socketId: null,
  notifications: [],
  connectionError: null,
  isConnecting: false,
};

// Store socket instance outside of Redux state to avoid serialization issues
let socketInstance = null;
// Async thunk for connecting to socket
export const connectSocket = createAsyncThunk(
  "websocket/connect",
  async ({ language }, { dispatch, rejectWithValue }) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found in localStorage");
      }

    const socket = io("https://api.houfalmanazel.com", {
  // autoConnect: false,
  // reconnection: false,
});

      return new Promise((resolve, reject) => {
        socket.on("connect", () => {
          console.log("Socket connected with ID:", socket.id);

          // Store socket instance outside Redux state
          socketInstance = socket;

          // Authenticate immediately after connection
          socket.emit("authenticate", token);
          console.log("Authentication token sent");

          resolve({
            socketId: socket.id,
          });
        });

        socket.on("disconnect", () => {
          console.log("Socket disconnected");
          socketInstance = null;
          dispatch(handleDisconnect());
        });

        socket.on("authenticated", (message) => {
          console.log("Socket authenticated:", message);
          toast.success(
            language === "en" ? "Successfully connected" : "تم الاتصال بنجاح",
            { autoClose: 3000 }
          );
        });

        socket.on("error", (message) => {
          console.error("Socket error:", message);
          toast.error(
            language === "en"
              ? `Connection error: ${message}`
              : `خطأ في الاتصال: ${message}`,
            { autoClose: false }
          );
          reject(new Error(message));
        });

        // Listen only to new-change event
        socket.on("new-change", (data) => {
          dispatch(handleNewChange({ data, language }));
        });
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// Async thunk for disconnecting socket
export const disconnectSocket = createAsyncThunk(
  "websocket/disconnect",
  async () => {
    if (socketInstance) {
      socketInstance.disconnect();
      socketInstance = null;
    }
    return null;
  }
);

const websocketSlice = createSlice({
  name: "websocket",
  initialState,
  reducers: {
    handleDisconnect: (state) => {
      state.isConnected = false;
      state.socketId = null;
    },

    handleNewChange: (state, action) => {
      const { data } = action.payload;
      console.log("New change received:", data);
      toast.success("There is new message");
    },

    clearNotifications: (state) => {
      state.notifications = [];
    },
    markNotificationAsRead: (state, action) => {
      const notificationId = action.payload;
      state.notifications = state.notifications.map((notification) =>
        notification.id === notificationId ||
        notification.notificationId === notificationId
          ? { ...notification, read: true }
          : notification
      );
    },
    setConnectionError: (state, action) => {
      state.connectionError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(connectSocket.pending, (state) => {
        state.isConnecting = true;
        state.connectionError = null;
      })
      .addCase(connectSocket.fulfilled, (state, action) => {
        state.isConnecting = false;
        state.isConnected = true;
        state.socketId = action.payload.socketId;
        state.connectionError = null;
      })
      .addCase(connectSocket.rejected, (state, action) => {
        state.isConnecting = false;
        state.isConnected = false;
        state.connectionError = action.payload;
      })
      .addCase(disconnectSocket.fulfilled, (state) => {
        state.isConnected = false;
        state.socketId = null;
      });
  },
});

// Export actions
export const {
  handleDisconnect,
  handleNewChange,
  clearNotifications,
  markNotificationAsRead,
  setConnectionError,
} = websocketSlice.actions;

// Selectors
export const selectIsConnected = (state) => state.websocket.isConnected;
export const selectSocketId = (state) => state.websocket.socketId;
export const selectNotifications = (state) => state.websocket.notifications;
export const selectUnreadCount = (state) =>
  state.websocket.notifications.filter((notification) => !notification.read)
    .length;
export const selectConnectionError = (state) => state.websocket.connectionError;
export const selectIsConnecting = (state) => state.websocket.isConnecting;

// Helper function to get socket instance (use sparingly)
export const getSocketInstance = () => socketInstance;

export default websocketSlice.reducer;
